import { MarkType, NodeType } from "../utils";
import { PlateElement, PlateLeaf } from "platejs/react";
import {
  MediaPlaceholder,
  PlateImageElement,
} from "../elements/MediaPlaceholder";
import {
  ColumnGroup,
  ColumnGroupStatic,
  ColumnItem,
  ColumnItemStatic,
} from "../elements/Columns";
import { Paragraph } from "../elements/Paragraph";
import { ReactNode } from "react";
import escapeHtml from "escape-html";
import { ScriptPlaceholder } from "../elements/ScriptPlaceholder";
import { withNodeProps } from "./withNodeProps";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const supportedNodeTypes = [
  MarkType.STRONG,
  MarkType.EMPHASIS,
  MarkType.UNDERLINE,
  MarkType.STRIKETHROUGH,
  MarkType.CODE,
  MarkType.HIGHLIGHT,
  NodeType.BLOCK_QUOTE,
  NodeType.HEADING_ONE,
  NodeType.HEADING_TWO,
  NodeType.HEADING_THREE,
  NodeType.HEADING_FOUR,
  NodeType.HEADING_FIVE,
  NodeType.HEADING_SIX,
  NodeType.PARAGRAPH,
  NodeType.UNORDERED_LIST,
  NodeType.ORDERED_LIST,
  NodeType.LIST_ITEM,
  NodeType.LINK,
  NodeType.THEMATIC_BREAK,
  NodeType.IMAGE,
  NodeType.MEDIA_PLACEHOLDER,
  NodeType.COLUMN_GROUP,
  NodeType.COLUMN_ITEM,
  NodeType.IFRAME,
  NodeType.DIV,
  NodeType.SCRIPT,
] as const;
type ISupportedNodeTypes = (typeof supportedNodeTypes)[number];

export const basePlateComponentOverrides: {
  [key in ISupportedNodeTypes]: (props) => ReactNode;
} = {
  [MarkType.STRONG]: (props) => <PlateLeaf as={"strong"} {...props} />,
  [MarkType.EMPHASIS]: (props) => <PlateLeaf as={"em"} {...props} />,
  [MarkType.UNDERLINE]: (props) => <PlateLeaf as={"u"} {...props} />,
  [MarkType.STRIKETHROUGH]: (props) => <PlateLeaf as={"s"} {...props} />,
  [MarkType.CODE]: (props) => <PlateLeaf as={"code"} {...props} />,
  [MarkType.HIGHLIGHT]: (props) => <PlateLeaf as={"mark"} {...props} />,
  [NodeType.BLOCK_QUOTE]: (props) => (
    <PlateElement as={"blockquote"} {...props} />
  ),
  [NodeType.HEADING_ONE]: (props) => <PlateElement as={"h1"} {...props} />,
  [NodeType.HEADING_TWO]: (props) => <PlateElement as={"h2"} {...props} />,
  [NodeType.HEADING_THREE]: (props) => <PlateElement as={"h3"} {...props} />,
  [NodeType.HEADING_FOUR]: (props) => <PlateElement as={"h4"} {...props} />,
  [NodeType.HEADING_FIVE]: (props) => <PlateElement as={"h5"} {...props} />,
  [NodeType.HEADING_SIX]: (props) => <PlateElement as={"h6"} {...props} />,
  [NodeType.PARAGRAPH]: (props) => <Paragraph {...props} />,
  [NodeType.UNORDERED_LIST]: (props) => <PlateElement as={"ul"} {...props} />,
  [NodeType.ORDERED_LIST]: (props) => <PlateElement as={"ol"} {...props} />,
  [NodeType.LIST_ITEM]: (props) => <PlateElement as={"li"} {...props} />,
  [NodeType.LINK]: (props) => <PlateElement as={"a"} {...props} />,
  [NodeType.THEMATIC_BREAK]: () => <hr contentEditable={false} />,
  [NodeType.IMAGE]: (props) => {
    const { url, alt } = props.element;
    return <PlateImageElement src={url} alt={alt} />;
  },
  [NodeType.MEDIA_PLACEHOLDER]: (props) => <MediaPlaceholder {...props} />,
  [NodeType.COLUMN_GROUP]: (props) => <ColumnGroup {...props} />,
  [NodeType.COLUMN_ITEM]: (props) => <ColumnItem {...props} />,
  [NodeType.IFRAME]: (props) => {
    return <iframe {...props.element.elementAttributes} />;
  },
  [NodeType.SCRIPT]: () => <ScriptPlaceholder />,
  [NodeType.DIV]: (props) => <PlateElement as={"div"} {...props} />,
};

export const staticPlateComponents: {
  [key in ISupportedNodeTypes]: (props) => ReactNode;
} = {
  [MarkType.STRONG]: (props) => (
    <strong {...withNodeProps(props)}>{props.children}</strong>
  ),
  [MarkType.EMPHASIS]: (props) => (
    <em {...withNodeProps(props)}>{props.children}</em>
  ),
  [MarkType.UNDERLINE]: (props) => (
    <u {...withNodeProps(props)}>{props.children}</u>
  ),
  [MarkType.STRIKETHROUGH]: (props) => (
    <s {...withNodeProps(props)}>{props.children}</s>
  ),
  [MarkType.CODE]: (props) => {
    return <code {...withNodeProps(props)}>{escapeHtml(props.text.text)}</code>;
  },
  [MarkType.HIGHLIGHT]: (props) => (
    <mark {...withNodeProps(props)}>{props.children}</mark>
  ),
  [NodeType.BLOCK_QUOTE]: (props) => (
    <blockquote {...withNodeProps(props)}>{props.children}</blockquote>
  ),
  [NodeType.HEADING_ONE]: (props) => (
    <h1 {...withNodeProps(props)}>{props.children}</h1>
  ),
  [NodeType.HEADING_TWO]: (props) => (
    <h2 {...withNodeProps(props)}>{props.children}</h2>
  ),
  [NodeType.HEADING_THREE]: (props) => (
    <h3 {...withNodeProps(props)}>{props.children}</h3>
  ),
  [NodeType.HEADING_FOUR]: (props) => (
    <h4 {...withNodeProps(props)}>{props.children}</h4>
  ),
  [NodeType.HEADING_FIVE]: (props) => (
    <h5 {...withNodeProps(props)}>{props.children}</h5>
  ),
  [NodeType.HEADING_SIX]: (props) => (
    <h6 {...withNodeProps(props)}>{props.children}</h6>
  ),
  [NodeType.PARAGRAPH]: (props) => {
    return <p {...withNodeProps(props)}>{props.children}</p>;
  },
  [NodeType.UNORDERED_LIST]: (props) => (
    <ul {...withNodeProps(props)}>{props.children}</ul>
  ),
  [NodeType.ORDERED_LIST]: (props) => (
    <ol {...withNodeProps(props)}>{props.children}</ol>
  ),
  [NodeType.LIST_ITEM]: (props) => (
    <li {...withNodeProps(props)}>{props.children}</li>
  ),
  [NodeType.LINK]: (props) => <a {...withNodeProps(props)}>{props.children}</a>,
  [NodeType.THEMATIC_BREAK]: () => <hr contentEditable={false} />,
  [NodeType.IMAGE]: (props) => (
    <img
      src={props.element.url}
      alt={props.element.alt}
      {...withNodeProps(props)}
    />
  ),
  [NodeType.COLUMN_GROUP]: (props) => <ColumnGroupStatic {...props} />,
  [NodeType.COLUMN_ITEM]: (props) => <ColumnItemStatic {...props} />,
  [NodeType.MEDIA_PLACEHOLDER]: (props) => {
    switch (props.element.mediaType) {
      case "img":
        return (
          <img
            data-id={props.element.id}
            data-media-type={props.element.mediaType}
            data-type={props.element.type}
          />
        );
      default:
        return <></>;
    }
  },
  [NodeType.IFRAME]: (props) => {
    return <iframe {...withNodeProps(props)} />;
  },
  [NodeType.SCRIPT]: (props) => {
    return <script {...withNodeProps(props)} />;
  },
  [NodeType.DIV]: (props) => (
    <div {...withNodeProps(props)}>{props.children}</div>
  ),
};
